import React, { useState, useEffect } from "react";
import { fetchVocabularyGroups, deleteVocabularyGroup } from "./adminVocabularyGroupService";
import VocabularyGroupForm from "./VocabularyGroupForm";
import { useToast } from "../../../common/toast/ToastContext";
import AdminPageLayout from "../../components/AdminPageLayout";

const BASE_ROOT = process.env.REACT_APP_BASE_URL_ROOT || process.env.REACT_APP_BASE_URL?.replace(/\/api$/, "");
console.log("[VocabularyGroupPage] BASE_ROOT:", BASE_ROOT, "| REACT_APP_BASE_URL_ROOT:", process.env.REACT_APP_BASE_URL_ROOT, "| REACT_APP_BASE_URL:", process.env.REACT_APP_BASE_URL);

const toPublicImgSrc = (url) => {
  if (!url) return "";
  const pub = url.replace(/\/api(?:\/admin)?\/media-images\//, "/api/public/media-images/");
  const src = pub.startsWith("http") ? pub : `${BASE_ROOT}${pub}`;
  console.log("[VocabularyGroupPage] img raw:", url, "→ pub:", pub, "→ resolved:", src);
  return src;
};

const AdminVocabularyGroupPage = () => {
  const { showToast } = useToast();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const size = 20;

  useEffect(() => { loadGroups(); }, [page, search]);

  const loadGroups = async () => {
    setLoading(true);
    const params = { page, size };
    if (search.trim()) params.search = search.trim();
    const result = await fetchVocabularyGroups(params);
    console.log("[VocabularyGroupPage] API response:", result.status, result.data);
    if (result.status === 200) {
      const content = result.data.content || [];
      console.log("[VocabularyGroupPage] groups sample imageUrls:", content.slice(0, 3).map(g => ({ id: g.id, name: g.name, imageUrl: g.imageUrl })));
      setGroups(content);
      setTotalPages(result.data.totalPages || 0);
      setTotalElements(result.data.totalElements || 0);
    } else {
      showToast(result.message || "Failed to load groups", "error");
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    const result = await deleteVocabularyGroup(id);
    if (result.status === 200) {
      showToast(result.message || "Deleted successfully", "success");
      loadGroups();
    } else {
      showToast(result.message || "Delete failed", "error");
    }
    setDeleteConfirm(null);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingGroup(null);
    loadGroups();
  };

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  return (
    <AdminPageLayout
      title="Vocabulary Groups"
      subtitle="Manage curated vocabulary group collections."
      instruction="Create groups to bundle related vocabulary words together. Each group can have an optional cover image and description shown to students."
      actions={
        <button className="adm-btn adm-btn-primary" onClick={() => { setEditingGroup(null); setShowForm(true); }}>
          + Add Group
        </button>
      }
    >
      {/* Search */}
      <div style={{ marginBottom: 16 }}>
        <input
          type="text"
          placeholder="Search groups..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          style={{ padding: "8px 14px", border: "1px solid #ddd", borderRadius: 8, fontSize: 14, width: 280, outline: "none" }}
        />
      </div>

      {/* Table */}
      {loading ? (
        <p className="adm-loading">Loading...</p>
      ) : groups.length === 0 ? (
        <p className="adm-empty">No vocabulary groups found.</p>
      ) : (
        <table className="announcement-table">
          <thead>
            <tr>
              <th>Image</th>
              <th>Name</th>
              <th>Description</th>
              <th>Words</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <tr key={group.id}>
                <td>
                  {group.imageUrl ? (
                    <img
                      src={toPublicImgSrc(group.imageUrl)}
                      alt=""
                      onError={(e) => console.error("[VocabularyGroupPage] img FAILED to load:", e.currentTarget.src, "| raw imageUrl:", group.imageUrl)}
                      style={{ width: 44, height: 44, objectFit: "contain", background: "#f5f5f5", borderRadius: 6, border: "1px solid #eee" }}
                    />
                  ) : (
                    <div style={{ width: 44, height: 44, background: "#f5f5f5", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#bbb" }}>—</div>
                  )}
                </td>
                <td style={{ fontWeight: 600 }}>{group.name}</td>
                <td style={{ color: "#666", fontSize: 13, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {group.description || "—"}
                </td>
                <td style={{ textAlign: "center" }}>
                  <span style={{ background: "#e8f4ff", color: "#0066cc", borderRadius: 12, padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>
                    {group.wordCount ?? group.vocabularies?.length ?? 0}
                  </span>
                </td>
                <td style={{ color: "#888", fontSize: 13 }}>{formatDate(group.createdAt)}</td>
                <td>
                  <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => { setEditingGroup(group); setShowForm(true); }}>Edit</button>
                  <button className="adm-btn adm-btn-danger adm-btn-sm" style={{ marginLeft: 6 }} onClick={() => setDeleteConfirm(group)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16, fontSize: 13, color: "#666" }}>
          <span>
            Showing {page * size + 1}–{Math.min((page + 1) * size, totalElements)} of {totalElements}
          </span>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button className="adm-btn adm-btn-ghost adm-btn-sm" disabled={page === 0} onClick={() => setPage(page - 1)}>‹ Prev</button>
            <span>Page {page + 1} / {totalPages}</span>
            <button className="adm-btn adm-btn-ghost adm-btn-sm" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>Next ›</button>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <VocabularyGroupForm
          group={editingGroup}
          onSuccess={handleFormSuccess}
          onCancel={() => { setShowForm(false); setEditingGroup(null); }}
        />
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 28, maxWidth: 420, width: "100%", margin: "0 16px" }}>
            <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 700 }}>Confirm Delete</h3>
            <p style={{ color: "#555", marginBottom: 24, fontSize: 14 }}>
              Are you sure you want to delete <strong>"{deleteConfirm.name}"</strong>? This action cannot be undone.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button className="adm-btn adm-btn-ghost" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="adm-btn adm-btn-danger" onClick={() => handleDelete(deleteConfirm.id)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </AdminPageLayout>
  );
};

export default AdminVocabularyGroupPage;
